// Utility functions for Minecraft item icons, text parsing, and date formatting

// Minecraft color code mapping (§0-§f and &0-&f)
const MC_COLORS = {
  '0': '#000000', // Black
  '1': '#0000AA', // Dark Blue
  '2': '#00AA00', // Dark Green
  '3': '#00AAAA', // Dark Aqua
  '4': '#AA0000', // Dark Red
  '5': '#AA00AA', // Dark Purple
  '6': '#FFAA00', // Gold
  '7': '#AAAAAA', // Gray
  '8': '#555555', // Dark Gray
  '9': '#5555FF', // Blue
  'a': '#55FF55', // Green
  'b': '#55FFFF', // Aqua
  'c': '#FF5555', // Red
  'd': '#FF55FF', // Light Purple
  'e': '#FFFF55', // Yellow
  'f': '#FFFFFF', // White
};

// Minecraft formatting codes
const MC_FORMATS = {
  'l': 'font-weight: bold;',
  'o': 'font-style: italic;',
  'n': 'text-decoration: underline;',
  'm': 'text-decoration: line-through;',
  'r': 'reset', // Reset formatting
};

/**
 * Parse Minecraft color/format codes and return React elements
 * Supports: §x§r§r§g§g§b§b (hex), §0-§f (colors), §l/o/n/m/r (formats), and & variants
 */
export function parseMinecraftText(text) {
  if (!text || typeof text !== 'string') return text;

  const segments = [];
  let currentColor = null;
  let currentFormats = [];
  let currentText = '';
  let i = 0;

  while (i < text.length) {
    const char = text[i];
    const nextChar = text[i + 1]?.toLowerCase();

    // Check for color/format codes (§ or &)
    if ((char === '§' || char === '&') && nextChar) {
      // Save current segment if exists
      if (currentText) {
        segments.push({
          text: currentText,
          color: currentColor,
          formats: [...currentFormats]
        });
        currentText = '';
      }

      // Check for hex color: §x§r§r§g§g§b§b
      if (nextChar === 'x' && text.length > i + 13) {
        const hexChars = [];
        let validHex = true;
        for (let j = 0; j < 6; j++) {
          const prefix = text[i + 2 + (j * 2)];
          const hexChar = text[i + 3 + (j * 2)];
          if ((prefix === '§' || prefix === '&') && /[0-9a-fA-F]/.test(hexChar)) {
            hexChars.push(hexChar);
          } else {
            validHex = false;
            break;
          }
        }
        if (validHex && hexChars.length === 6) {
          currentColor = '#' + hexChars.join('');
          i += 14; // Skip §x§r§r§g§g§b§b
          continue;
        }
      }

      // Standard color code
      if (MC_COLORS[nextChar]) {
        currentColor = MC_COLORS[nextChar];
        i += 2;
        continue;
      }

      // Format code
      if (MC_FORMATS[nextChar]) {
        if (nextChar === 'r') {
          currentColor = null;
          currentFormats = [];
        } else {
          currentFormats.push(MC_FORMATS[nextChar]);
        }
        i += 2;
        continue;
      }
    }

    currentText += char;
    i++;
  }

  // Add final segment
  if (currentText) {
    segments.push({
      text: currentText,
      color: currentColor,
      formats: [...currentFormats]
    });
  }

  return segments;
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Strip all Minecraft/Skript color and format codes, returning plain text.
 */
export function stripMinecraftCodes(text) {
  if (!text || typeof text !== 'string') return text || '';
  // Remove §x§r§r§g§g§b§b hex, §/& color/format codes, and <#RRGGBB>/<##RRGGBB> hex tags
  return text
    .replace(/[§&]x([§&][0-9a-fA-F]){6}/g, '')
    .replace(/[§&][0-9a-fk-or]/gi, '')
    .replace(/<#[0-9a-fA-F]{6}>/g, '')
    .replace(/<##[0-9a-fA-F]{6}>/g, '');
}

/**
 * Render Minecraft text as HTML string (for dangerouslySetInnerHTML)
 */
export function renderMinecraftTextHTML(text) {
  const segments = parseMinecraftText(text);
  if (!Array.isArray(segments)) return escapeHtml(text || '');

  return segments.map(seg => {
    const styles = [];
    if (seg.color) styles.push(`color: ${seg.color}`);
    styles.push(...seg.formats);

    const styleStr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';
    return `<span${styleStr}>${escapeHtml(seg.text)}</span>`;
  }).join('');
}

/**
 * Get the URL for a Minecraft item icon
 * Uses mc.nerothe.com API for item sprites
 */
export function getMinecraftItemIcon(itemId) {
  if (!itemId || typeof itemId !== 'string') return '/logo.png';

  // Clean the item ID (remove namespace if present)
  const cleanId = itemId.replace('minecraft:', '');

  // Use a public Minecraft item sprite API
  return `https://mc.nerothe.com/img/1.20.4/item/${cleanId}.png`;
}

/**
 * Format UNIX timestamp to readable date string
 */
export function formatEventDate(timestamp) {
  if (!timestamp) return 'Date TBD';

  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(date);
}

/**
 * Get relative time string (e.g., "in 5d 1h")
 * Shows top 2 time units only
 */
export function getRelativeTime(timestamp) {
  if (!timestamp) return 'Coming soon';

  const now = Math.floor(Date.now() / 1000);
  const diff = timestamp - now;

  if (diff < 0) return 'Past event';

  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  // Build parts array with top 2 non-zero units
  let parts = [];

  if (days > 0) {
    parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
  } else if (hours > 0) {
    parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
  } else if (minutes > 0) {
    parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
  } else {
    parts.push(`${seconds}s`);
  }

  return `in ${parts.join(' ')}`;
}

/**
 * Get precise countdown string (e.g., "(in 12h 21m 29s)")
 * Returns null if event is in the past
 */
export function getCountdown(timestamp) {
  if (!timestamp) return null;

  const now = Math.floor(Date.now() / 1000);
  const diff = timestamp - now;

  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  let parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);

  return `(in ${parts.join(' ')})`;
}

/**
 * Format short date (e.g., "Dec 25")
 */
export function formatShortDate(timestamp) {
  if (!timestamp) return 'TBD';

  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(date);
}

/**
 * Format time only (e.g., "3:00 PM EST")
 */
export function formatTime(timestamp) {
  if (!timestamp) return 'TBD';

  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(date);
}
